import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { AccountUtilityAddState } from '../../../interface/account-management-utility.interface';
import { AccountParameters } from '../../../interface/account-management.interface';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { fetchAllAccounts } from '../../../redux/slices/UtilityProviderManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import {
  emailValidation,
  longTextValidation,
  passwordValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import utilityAccountApi from '../../../utils/api/utility-provider';

/**
 * Add Account function component
 * @param param0
 * @returns
 */
function AddUtilityAccount({
  closeModal,
  sidebarClosed,
}: {
  closeModal: (value: string) => void;
  sidebarClosed: () => void;
}) {
  const { t } = useTranslation('account');
  const dispatch = useAppDispatch();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
  const [accountDetails, setAccountDetails] = useState<AccountUtilityAddState>({
    name: '',
    email: '',
    address: '',
    password: '',
    rePassword: '',
  });

  /**
   * Handle the change function
   * @param name
   * @param value
   */
  const handleChangeFunction = (name: string, value: string[] | File[] | string | number) => {
    setAccountDetails((previous: AccountUtilityAddState) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the submit function
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    let errorMessage = false;
    if (
      !emailValidation(accountDetails.email) ||
      !stringValidation(1, 100, accountDetails.name) ||
      !longTextValidation(500, accountDetails.address) ||
      accountDetails.email === '' ||
      !passwordValidation(accountDetails.password) ||
      accountDetails.rePassword !== accountDetails.password
    ) {
      errorMessage = true;
    }
    if (!errorMessage) {
      setDisabled(true);
      const request: AccountParameters = {
        name: accountDetails.name,
        email: accountDetails.email.toLowerCase(),
        password: accountDetails.password,
        address: accountDetails.address,
      };
      utilityAccountApi
        .createUtilityAccounts(request)
        .then(() => {
          setDisabled(true);
          dispatch(
            fetchAllAccounts({
              page: DefaultApiParameters.page,
              limit: DefaultApiParameters.limit,
            }),
          );
          closeModal(t('AccountAddedSuccessfully'));
          sidebarClosed();
        })
        .catch((error) => {
          setDisabled(false);
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  return (
    <div className="account-form">
      <div className="form-header">
        <h3>{t('addUtility')}</h3>
      </div>
      <div
        className="form-body"
        style={{ overflowY: 'scroll', height: window.innerHeight >= 700 ? 347 : 352 }}>
        <div className="form-row">
          <div className="form-field">
            <CustomInput
              name="name"
              inputLabel={t('userName')}
              type="text"
              placeholder={t('enterUserName')}
              value={accountDetails.name}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={showFormErrors && !stringValidation(1, 100, accountDetails.name)}
              errorMessage={t('userNameRequired')}
            />
          </div>
          <div className="form-field">
            <CustomInput
              name="email"
              inputLabel={t('emailId')}
              type="text"
              placeholder={t('enterEmail')}
              value={accountDetails.email}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={
                showFormErrors &&
                (accountDetails.email === '' || !emailValidation(accountDetails.email))
              }
              errorMessage={t('emailRequiredOrInvalid')}
            />
          </div>
        </div>

        <div className="form-row-full">
          <div className="form-field">
            <CustomInput
              name="address"
              inputLabel={t('address')}
              type="text"
              placeholder={t('enterAddress')}
              value={accountDetails.address}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={showFormErrors && !longTextValidation(500, accountDetails.address)}
              errorMessage={t('addressRequired')}
            />
          </div>
        </div>

        <hr className="form-divider" />

        <div className="form-row">
          <div className="form-field">
            <CustomInput
              name="password"
              inputLabel={t('setPassword')}
              type="password"
              placeholder={t('enterPassword')}
              value={accountDetails.password}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={showFormErrors && !passwordValidation(accountDetails.password)}
              errorMessage={t('passwordRequiredInvalid')}
            />
          </div>
          <div className="form-field">
            <CustomInput
              name="rePassword"
              inputLabel={t('reEnterPassword')}
              type="password"
              placeholder={t('enterPassword')}
              value={accountDetails.rePassword}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={
                showFormErrors &&
                !passwordValidation(accountDetails.rePassword) &&
                (accountDetails.password !== accountDetails.rePassword ||
                  accountDetails.rePassword === '')
              }
              errorMessage={
                accountDetails.rePassword.length === 0
                  ? t('reEnterPasswordRequiredOrInvalid')
                  : t('passwordsMustMatch')
              }
            />
          </div>
        </div>
      </div>

      <div className="form-bottom-button">
        <div className="table-button-item">
          <MyButton
            label={t('cancel')}
            buttonType="outline"
            onClickFunc={() => {
              closeModal('');
              sidebarClosed();
            }}
          />
        </div>
        <div className="table-button-item">
          <MyButton
            label={t('submit')}
            buttonType="submit"
            loading={disabled}
            disabled={disabled}
            onClickFunc={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}

export default AddUtilityAccount;
